<script lang="ts">
  import * as _ from "lodash";
  import TrackingContext from "@shared/svelte/Components/Tracking/TrackingContext.svelte";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { DealTierProductEntity } from "@shared/services/event_tracking/entities";
  import DemoSignupModal from "@shared/svelte/Components/Layout/DemoHeader/DemoSignupModal.svelte";
  import { uniq } from "lodash";
  import { url, goto, redirect } from "@roxi/routify";
  import { modernRoutes, prepUrlFromUIRouter } from "@shared/svelte/modernRouting";
  import type { BaseEntityClass } from "@shared/services/event_tracking/entities/base_entity_class";
  import {
    isStateAllowedForRole,
    showPreviewForbiddenFlash,
  } from "@retailer/services/preview_forbidden_flash";

  export let stateName: string;
  export let params = {};
  export let opts: any = {};
  export let classes = "";
  export let stateParamsToAdd = [];
  export let trackingParams = null;
  export let asyncTrackingParams = null;
  export let eventName: string = null;
  export let useTrackClick: boolean = false;
  export let tableRow: boolean = false;
  export let id: string = "";
  export let disable: boolean = false;
  export let button: boolean = false;
  export let buttonElement: boolean = false;
  export let style: string = "";
  export let target: string = "";
  export let useDiv: boolean = false;
  export let targetObjectType: string | null = null;
  export let onClickCB = () => {};
  export let rel = "";
  export let grabbed: boolean = false;
  export let stopPropagation: boolean = false;

  const { trackClick, trackEvent } = EventTracking;

  const ignoreInDistributorApp = ["layout.product_detail", "layout.product_listing"];

  function ignore() {
    //@ts-ignore
    if (
      _.get(window, "C.APP_NAME") == "distributor" &&
      _.includes(ignoreInDistributorApp, stateName)
    ) {
      return true;
    } else if (disable) {
      return true;
    }
  }

  const injectAsyncTrackingParams = (tracking: any): BaseEntityClass[] => {
    return uniq(tracking.concat(asyncTrackingParams || []));
  };

  const getUrl = function () {
    if (window["modern_mode"]) {
      if (modernRoutes(stateName)) {
        params = _.omitBy(params, function (x) {
          return x === null || x === undefined || x === "null";
        });
        return $url(modernRoutes(stateName), params);
      } else {
        return prepUrlFromUIRouter(stateName, params, {});
      }
    } else {
      if (modernRoutes(stateName)) {
        return prepUrlFromUIRouter(stateName, params, {});
      } else {
        return window["stateService"].href(stateName, params, opts);
      }
    }
  };

  const demoNavigationInterrupt = function () {
    if (
      window.location.href.includes("/demo/storefront") &&
      (stateName === "layout.product_detail" || stateName === "layout.product_listing")
    ) {
      window["svelteModal"](DemoSignupModal, {});
      return true;
    }
    return false;
  };

  const prepTrackClickParams = entities => {
    if (targetObjectType !== null) {
      if (entities && entities.length > 0) {
        return [targetObjectType, entities];
      } else {
        return [targetObjectType];
      }
    } else {
      return [null, entities];
    }
  };

  function onclick(e: any, tracking: any) {
    if (ignore()) {
      return false;
    }

    if (demoNavigationInterrupt()) {
      e.preventDefault();
      return false;
    }

    if (stopPropagation) {
      e.stopPropagation();
    }

    if (trackingParams && trackingParams.discountClick) {
      const entity = new DealTierProductEntity({
        product_id: trackingParams.product_id,
        deal_tier_id: trackingParams.deal_tier_id,
        distributor_id: trackingParams.distributor_id,
        product_group_id: trackingParams.product_group_id,
        sku: trackingParams.sku,
      });
      trackEvent("link_click", entity);
    } else if (tracking.length > 0) {
      if (eventName) {
        trackEvent(
          // @ts-ignore
          ...[eventName, injectAsyncTrackingParams(tracking), targetObjectType].filter(
            param => param !== null,
          ),
        );
      }

      if (useTrackClick) {
        // @ts-ignore
        trackClick(...prepTrackClickParams(injectAsyncTrackingParams(tracking)));
      }
    } else if (useTrackClick) {
      // Call trackClick even when there are no tracking entities
      // @ts-ignore
      trackClick(...prepTrackClickParams([]));
    }

    if (!e.metaKey && !e.shiftKey) {
      e.preventDefault();
      onClickCB();
      if (window["modern_mode"]) {
        if (modernRoutes(stateName)) {
          params = _.omitBy(params, function (x) {
            return x === null || x === undefined || x === "null";
          });
          if (opts.redirect) {
            $redirect(modernRoutes(stateName), params);
          } else {
            $goto(modernRoutes(stateName), params);
          }
        } else {
          if (!isStateAllowedForRole(_.get(window, "$rootScope.user.role"), stateName)) {
            showPreviewForbiddenFlash();
            return;
          }
          window.location.href = prepUrlFromUIRouter(stateName, params, {});
        }
      } else {
        if (modernRoutes(stateName)) {
          if (!isStateAllowedForRole(_.get(window, "$rootScope.user.role"), stateName)) {
            showPreviewForbiddenFlash();
            return;
          }
          window.location.href = prepUrlFromUIRouter(stateName, params, {});
        } else {
          window["stateService"].go(stateName, _.merge(params, _.omit(tracking, "type")), opts);
        }
      }
    }
  }

  $: {
    stateParamsToAdd.forEach(function (toAdd) {
      //@ts-ignore
      params[toAdd] = window.stateParams[toAdd];
    });
  }
  $: href = getUrl();
</script>

<style>
  a.button,
  a.button:hover {
    text-decoration: none;
  }
  a.button {
    padding: 0;
  }

  .grabbing {
    cursor: grabbing;
  }
</style>

<TrackingContext entities={trackingParams} let:trackingContext>
  {#if tableRow}
    <tr
      on:click={e => onclick(e, trackingContext.entities)}
      class={classes}
      class:grabbing={grabbed}
      {id}
    >
      <slot />
    </tr>
  {:else if useDiv}
    <div
      {href}
      on:click={e => onclick(e, trackingContext.entities)}
      class={classes}
      class:button
      class:grabbing={grabbed}
      {id}
      {style}
      {target}
    >
      <slot />
    </div>
  {:else if buttonElement}
    <button
      {href}
      on:click={e => onclick(e, trackingContext.entities)}
      class={classes}
      class:button
      class:grabbing={grabbed}
      {id}
      {style}
      {target}
    >
      <slot />
    </button>
  {:else}
    <a
      {href}
      {rel}
      on:click={e => onclick(e, trackingContext.entities)}
      class={classes}
      class:button
      class:grabbing={grabbed}
      {id}
      {style}
      {target}
    >
      <slot />
    </a>
  {/if}
</TrackingContext>
